"use client";

// /experiment fork of ReferShareModal.
// Same simple/clean style as the Sushi Tuesday banner: solid color block,
// white sentence-case sans-serif, terminal period, one rounded pill, icon-
// only share row. No stadium photo, no halos, no holographic layers.
// User has no place to mistake what they're sending.

import { createContext, useContext, useEffect, useState } from "react";
import Image from "next/image";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { XTwitterIcon, InstagramIcon, TelegramIcon, WhatsAppIcon, UsdcIcon, PicksPIcon } from "@/components/ui/icons";

interface Props {
  open: boolean;
  onClose: () => void;
  /** When set, render this poster variant (1-based) and hide the dev picker. */
  forcePosterVariant?: number;
  /** Fired when the user copies the link or taps any share channel. */
  onShare?: () => void;
  /** The referral link the poster prints and the pill copies, no scheme.
      Defaults to the demo account, so existing callers are unchanged. */
  link?: string;
}

const REF_LINK = "go.getpicks.app/r/yourcode";

/** The link on the poster, read by whichever variant draws the footer. */
const LinkContext = createContext(REF_LINK);
const SHARE_TEXT =
  "I'm playing GetPicks. Join me and claim a $50 welcome bonus, just play $5 to unlock.";

const FONT: React.CSSProperties = {
  fontFamily: "var(--font-open-sauce), system-ui, sans-serif",
};

const POSTER_STORAGE_KEY = "alexey:refer-poster-variant";
const POSTER_LABELS = [
  "V1 · Chip top-right",
  "V2 · Centered axis",
  "V3 · Stacked top",
  "V4 · Side-by-side",
  "V5 · Headline dominant",
  "V6 · Chip bottom-right",
  "V7 · OG landscape 2:1",
] as const;

export function ReferShareModalExperiment({ open, onClose, forcePosterVariant, onShare, link }: Props) {
  const refLink = link ?? REF_LINK;
  const SHARE_URL = `https://${refLink}`;
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [storedPosterVariant, setPosterVariant] = useState(1);
  const [posterPickerOpen, setPosterPickerOpen] = useState(false);
  const posterVariant = forcePosterVariant ?? storedPosterVariant;
  const showPicker = forcePosterVariant === undefined;

  useEffect(() => {
    try {
      const stored = localStorage.getItem(POSTER_STORAGE_KEY);
      if (stored !== null) {
        const n = Number(stored);
        if (n >= 1 && n <= POSTER_LABELS.length) setPosterVariant(n);
      }
    } catch {}
  }, []);

  const pickPosterVariant = (n: number) => {
    setPosterVariant(n);
    setPosterPickerOpen(false);
    try {
      localStorage.setItem(POSTER_STORAGE_KEY, String(n));
    } catch {}
  };

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    setEntered(false);
    const id = window.setTimeout(() => setMounted(false), 280);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!mounted || !open) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [mounted, open]);

  useEffect(() => {
    if (!mounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [mounted, onClose]);

  if (!mounted) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(SHARE_URL).then(
      () => {
        setCopied(true);
        toast.success("Link copied");
        setTimeout(() => setCopied(false), 2000);
        onShare?.();
      },
      () => toast.error("Couldn't copy"),
    );
  };

  const handleShare = (channel: "x" | "telegram" | "instagram" | "whatsapp") => {
    if (channel === "instagram") {
      navigator.clipboard.writeText(`${SHARE_TEXT} ${SHARE_URL}`).then(
        () => toast.success("Caption copied. Paste it in Instagram"),
        () => toast.error("Couldn't copy"),
      );
      window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
      onShare?.();
      return;
    }
    const links = {
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(SHARE_URL)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(SHARE_URL)}&text=${encodeURIComponent(SHARE_TEXT)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${SHARE_URL}`)}`,
    };
    window.open(links[channel], "_blank", "noopener,noreferrer");
    onShare?.();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Share referral"
      style={FONT}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className={cn(
          "absolute inset-0 bg-black/85 backdrop-blur-sm transition-opacity duration-300 ease-out",
          entered ? "opacity-100" : "opacity-0",
        )}
      />

      <div
        className={cn(
          "relative flex w-full max-w-[380px] flex-col gap-4 transition-all ease-out",
          entered
            ? "opacity-100 translate-y-0 scale-100 duration-[420ms]"
            : "opacity-0 translate-y-4 scale-[0.96] duration-200",
        )}
      >
        <LinkContext.Provider value={refLink}>
          <Poster variant={posterVariant} />
        </LinkContext.Provider>

        {/* Poster variant picker — Alexey-only dev control */}
        {showPicker && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setPosterPickerOpen((o) => !o)}
            className="flex w-full items-center justify-between rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70 hover:bg-white/10"
          >
            <span>Layout · {POSTER_LABELS[posterVariant - 1]}</span>
            <span aria-hidden>{posterPickerOpen ? "▴" : "▾"}</span>
          </button>
          {posterPickerOpen && (
            <ul className="absolute left-0 right-0 top-full z-10 mt-1 overflow-hidden rounded-md border border-white/15 bg-black/95 py-1 backdrop-blur-md">
              {POSTER_LABELS.map((label, i) => {
                const id = i + 1;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => pickPosterVariant(id)}
                      className={cn(
                        "flex w-full items-center justify-between px-3 py-1.5 text-left text-[11px] font-medium tracking-tight text-white/85 hover:bg-white/10",
                        id === posterVariant && "bg-white/10",
                      )}
                    >
                      <span>{label}</span>
                      {id === posterVariant && <span className="text-white">●</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        )}

        {/* ─── Share controls — single primary white pill + icon-only row */}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className="flex h-11 items-center justify-center rounded-full bg-white text-[13px] font-semibold tracking-tight"
            style={{ color: "hsl(335 78% 48%)" }}
          >
            {copied ? "Copied" : "Copy link"}
          </button>
          <div className="grid grid-cols-4 gap-2">
            <button
              type="button"
              onClick={() => handleShare("x")}
              aria-label="Share to X"
              className="flex h-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/85 hover:bg-white/10"
            >
              <XTwitterIcon className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => handleShare("instagram")}
              aria-label="Share to Instagram"
              className="flex h-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/85 hover:bg-white/10"
            >
              <InstagramIcon className="size-[18px]" />
            </button>
            <button
              type="button"
              onClick={() => handleShare("whatsapp")}
              aria-label="Share to WhatsApp"
              className="flex h-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/85 hover:bg-white/10"
            >
              <WhatsAppIcon className="size-[18px]" />
            </button>
            <button
              type="button"
              onClick={() => handleShare("telegram")}
              aria-label="Share to Telegram"
              className="flex h-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/85 hover:bg-white/10"
            >
              <TelegramIcon className="size-[18px]" />
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mt-1 h-9 text-[11px] font-medium tracking-tight text-white/55 hover:text-white/80"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Shared poster primitives ─────────────────────────────────────────── */

const PINK = "hsl(335 78% 48%)";
const PINK_DARK = "hsl(335 80% 36%)";

function PosterShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative aspect-[3/4] w-full overflow-hidden rounded-[20px]"
      style={{ background: PINK }}
    >
      <PosterDarkAccent darkBg={PINK_DARK} />
      <PosterHalftone />
      {children}
    </div>
  );
}

/* Halftone dot HUD — subtle white dot grid across the whole surface, soft
   overlay blend so it reads as printed paper texture, not loud pattern. */
function PosterHalftone() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage:
          "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1.4px)",
        backgroundSize: "9px 9px",
        opacity: 0.22,
        mixBlendMode: "overlay",
      }}
    />
  );
}

/* Dark-accent wash — two stacked diagonal gradients ease the base into a
   near-black brand tone toward the lower-right. Top layer is black for
   weight; under-layer keeps a hint of the brand hue. No hard edge. */
function PosterDarkAccent({ darkBg }: { darkBg: string }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        background: `
          linear-gradient(155deg, transparent 0%, transparent 35%, rgba(0,0,0,0.85) 100%),
          linear-gradient(155deg, transparent 0%, transparent 30%, ${darkBg} 100%)
        `,
        opacity: 0.9,
      }}
    />
  );
}

function PosterHeader({ align = "between" }: { align?: "between" | "center" }) {
  if (align === "center") {
    return (
      <div className="absolute inset-x-6 top-6 flex flex-col items-center gap-1.5">
        <span className="relative block h-[18px] w-[117px]">
          <Image src="/getpicks-logo.svg" alt="GetPicks" fill sizes="117px" className="object-contain brightness-0 invert" priority />
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/80">Referral</span>
      </div>
    );
  }
  return (
    <div className="absolute inset-x-6 top-6 flex items-center justify-between">
      <span className="relative block h-[18px] w-[117px]">
        <Image src="/getpicks-logo.svg" alt="GetPicks" fill sizes="117px" className="object-contain object-left brightness-0 invert" priority />
      </span>
      <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/80">Referral</span>
    </div>
  );
}

function FiftyChip({
  size = 138,
  fontSize = 46,
  rotate = -10,
}: {
  size?: number;
  fontSize?: number;
  rotate?: number;
}) {
  return (
    <div
      aria-hidden
      className="pointer-events-none flex flex-col items-center justify-center"
      style={{
        transform: `rotate(${rotate}deg)`,
        width: size,
        height: size,
        background: "white",
        borderRadius: size * 0.16,
        boxShadow: "0 8px 0 rgba(0,0,0,0.18), 0 0 0 5px white",
      }}
    >
      <span className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: PINK }}>up to</span>
      <span className="mt-0.5 font-bold leading-none tracking-[-0.03em]" style={{ color: PINK, fontSize }}>125&times;</span>
    </div>
  );
}

/* Small yellow companion chip — the payout currency (USDC), paired with the
   "up to 125×" chip. Yellow per the codified brand palette; smaller scale
   so it reads as the secondary element. */
function FiveChip({
  size = 68,
  fontSize = 19,
  rotate = 14,
}: {
  size?: number;
  fontSize?: number;
  rotate?: number;
}) {
  return (
    <div
      aria-hidden
      className="pointer-events-none flex flex-col items-center justify-center"
      style={{
        transform: `rotate(${rotate}deg)`,
        width: size,
        height: size,
        background: "#ffd45e",
        borderRadius: size * 0.18,
        boxShadow: "0 5px 0 rgba(0,0,0,0.18), 0 0 0 4px #ffd45e",
      }}
    >
      <span className="font-bold leading-none tracking-[-0.02em] text-black" style={{ fontSize }}>USDC</span>
      <span className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.16em] text-black/75">Payout</span>
    </div>
  );
}

function PosterFooter() {
  return (
    <div
      className="absolute inset-x-5 bottom-5 rounded-[14px] px-4 py-3"
      style={{ background: "rgba(0,0,0,0.18)" }}
    >
      <span className="relative block h-[12px] w-[64px]">
        <Image src="/getpicks-logo.svg" alt="GetPicks" fill sizes="64px" className="object-contain object-left brightness-0 invert opacity-80" />
      </span>
      <div className="mt-2 text-[15px] font-bold tracking-tight text-white">{useContext(LinkContext)}</div>
      <div className="mt-2 flex items-center gap-1.5 text-[11px] font-medium tracking-tight text-white/80">
        <span>Win in</span>
        <UsdcIcon aria-hidden className="size-3" />
        <span>USDC on</span>
        <PicksPIcon className="h-[9px] w-auto text-white" />
        <span>GetPicks.</span>
      </div>
    </div>
  );
}

/* ─── Poster variants ────────────────────────────────────────────────────
   Same locked text, same $50 chip, same Picks branding. Only chip placement,
   headline alignment, and composition change between them. */

function Poster({ variant }: { variant: number }) {
  switch (variant) {
    case 2: return <PosterCenteredAxis />;
    case 3: return <PosterStackedTop />;
    case 4: return <PosterSideBySide />;
    case 5: return <PosterHeadlineDominant />;
    case 6: return <PosterChipBottomRight />;
    case 7: return <PosterOgLandscape />;
    default: return <PosterChipTopRight />;
  }
}

/* V1 — Original. Chip top-right, headline middle-left, footer slab. */
function PosterChipTopRight() {
  return (
    <PosterShell>
      <PosterHeader />
      <div className="absolute" style={{ right: 24, top: 92 }}>
        <FiftyChip />
      </div>
      <div className="absolute" style={{ left: 14, top: 154 }}>
        <FiveChip />
      </div>
      <div className="absolute inset-x-6" style={{ top: 240 }}>
        <h2 className="text-[52px] font-bold leading-[0.9] tracking-[-0.025em] text-white">
          You&rsquo;re<br />invited.
        </h2>
        <p className="mt-4 text-[15px] font-semibold leading-[1.25] tracking-tight text-white">
          Play $5 to unlock the bonus.
        </p>
      </div>
      <PosterFooter />
    </PosterShell>
  );
}

/* V2 — Centered axis. Header centered, chip centered, headline centered. */
function PosterCenteredAxis() {
  return (
    <PosterShell>
      <PosterHeader align="center" />
      <div className="absolute left-1/2 -translate-x-1/2" style={{ top: 110 }}>
        <FiftyChip rotate={-6} />
      </div>
      <div className="absolute" style={{ left: "62%", top: 192 }}>
        <FiveChip rotate={18} />
      </div>
      <div className="absolute inset-x-6 text-center" style={{ top: 264 }}>
        <h2 className="text-[48px] font-bold leading-[0.92] tracking-[-0.025em] text-white">
          You&rsquo;re<br />invited.
        </h2>
        <p className="mt-4 text-[14px] font-semibold leading-[1.3] tracking-tight text-white">
          Play $5 to unlock the bonus.
        </p>
      </div>
      <PosterFooter />
    </PosterShell>
  );
}

/* V3 — Stacked top. Chip large on top-center, headline directly under. */
function PosterStackedTop() {
  return (
    <PosterShell>
      <PosterHeader />
      <div className="absolute left-1/2 -translate-x-1/2" style={{ top: 56 }}>
        <FiftyChip size={156} fontSize={62} rotate={4} />
      </div>
      <div className="absolute" style={{ right: 30, top: 174 }}>
        <FiveChip rotate={-16} />
      </div>
      <div className="absolute inset-x-6" style={{ top: 246 }}>
        <h2 className="text-[52px] font-bold leading-[0.9] tracking-[-0.025em] text-white">
          You&rsquo;re<br />invited.
        </h2>
        <p className="mt-4 text-[14px] font-semibold leading-[1.3] tracking-tight text-white">
          Play $5 to unlock the bonus.
        </p>
      </div>
      <PosterFooter />
    </PosterShell>
  );
}

/* V4 — Side-by-side. Chip top-left, headline right-aligned next to it. */
function PosterSideBySide() {
  return (
    <PosterShell>
      <PosterHeader />
      <div className="absolute" style={{ left: 24, top: 92 }}>
        <FiftyChip rotate={8} size={120} fontSize={48} />
      </div>
      <div className="absolute" style={{ left: 116, top: 200 }}>
        <FiveChip rotate={-10} size={60} fontSize={20} />
      </div>
      <div className="absolute" style={{ right: 24, top: 100, width: 200 }}>
        <h2 className="text-right text-[40px] font-bold leading-[0.92] tracking-[-0.025em] text-white">
          You&rsquo;re<br />invited.
        </h2>
      </div>
      <div className="absolute inset-x-6" style={{ top: 250 }}>
        <p className="text-[15px] font-semibold leading-[1.3] tracking-tight text-white">
          Play $5 to unlock the bonus.
        </p>
      </div>
      <PosterFooter />
    </PosterShell>
  );
}

/* V5 — Headline dominant. Massive headline fills the card; small chip is
       a corner accent. Most editorial, fewest interruptions. */
function PosterHeadlineDominant() {
  return (
    <PosterShell>
      <PosterHeader />
      <div className="absolute inset-x-6" style={{ top: 80 }}>
        {/* Headline scales with viewport so "You're / invited." never gets
            sliced on narrow modal widths (Telegram mini-app, inner-fold). */}
        <h2
          className="font-bold leading-[0.86] tracking-[-0.03em] text-white"
          style={{ fontSize: "clamp(44px, 18vw, 88px)" }}
        >
          You&rsquo;re<br />invited.
        </h2>
        <p className="mt-5 text-[15px] font-semibold leading-[1.25] tracking-tight text-white">
          Predict more or less on player stats.
        </p>
      </div>
      {/* Chips — "up to 125×" card + USDC coin companion, bottom-right. */}
      <div className="absolute" style={{ right: 24, bottom: 142 }}>
        <FiftyChip size={92} fontSize={36} rotate={-14} />
      </div>
      <div
        className="absolute"
        style={{ right: 112, bottom: 160, transform: "rotate(18deg)" }}
      >
        <UsdcIcon className="size-[54px] drop-shadow-[0_5px_0_rgba(0,0,0,0.18)]" />
      </div>
      <PosterFooter />
    </PosterShell>
  );
}

/* V6 — Chip bottom-right. Headline anchored top, chip above the footer
       slab as a bridge between message and link. */
function PosterChipBottomRight() {
  return (
    <PosterShell>
      <PosterHeader />
      <div className="absolute inset-x-6" style={{ top: 76 }}>
        <h2 className="text-[56px] font-bold leading-[0.9] tracking-[-0.025em] text-white">
          You&rsquo;re<br />invited.
        </h2>
        <p className="mt-4 text-[15px] font-semibold leading-[1.25] tracking-tight text-white">
          Play $5 to unlock the bonus.
        </p>
      </div>
      <div className="absolute" style={{ right: 28, bottom: 138 }}>
        <FiftyChip size={120} fontSize={48} rotate={6} />
      </div>
      <div className="absolute" style={{ left: 28, bottom: 152 }}>
        <FiveChip rotate={-12} />
      </div>
      <PosterFooter />
    </PosterShell>
  );
}

/* ─── Landscape shell — 2:1 for OG / social link-preview unfurls ─────────── */
function PosterShellLandscape({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative aspect-[2/1] w-full overflow-hidden rounded-[20px]"
      style={{ background: PINK }}
    >
      <PosterDarkAccent darkBg={PINK_DARK} />
      <PosterHalftone />
      {children}
    </div>
  );
}

/* V7 — OG link-preview landscape (2:1). Wide composition: brand + headline +
   referral link on the left, $50/$5 chips clustered on the right. Authored at
   the modal's preview width; export at a higher DPR for full OG resolution
   (everything scales uniformly). */
function PosterOgLandscape() {
  return (
    <PosterShellLandscape>
      {/* Compact header */}
      <div className="absolute inset-x-5 top-4 flex items-center justify-between">
        <span className="relative block h-[14px] w-[94px]">
          <Image src="/getpicks-logo.svg" alt="GetPicks" fill sizes="94px" className="object-contain object-left brightness-0 invert" priority />
        </span>
        <span className="text-[9px] font-semibold uppercase tracking-[0.24em] text-white/80">Referral</span>
      </div>

      {/* Left — headline + subline */}
      <div className="absolute" style={{ left: 20, top: 34, width: 215 }}>
        <h2 className="font-bold leading-[0.9] tracking-[-0.025em] text-white" style={{ fontSize: 25 }}>
          You&rsquo;re<br />invited.
        </h2>
        <p className="mt-1 font-semibold leading-[1.2] tracking-tight text-white" style={{ fontSize: 12 }}>
          Play $5 to unlock the bonus.
        </p>
      </div>

      {/* Left bottom — product framing (no referral link on the social image) */}
      <div className="absolute" style={{ left: 20, bottom: 14, width: 252 }}>
        <div className="font-semibold leading-[1.25] tracking-tight text-white/90" style={{ fontSize: 12 }}>
          Predict more or less on player stats.{" "}
          <span className="whitespace-nowrap">Win up to 125&times;.</span>
        </div>
      </div>

      {/* Right — chip cluster */}
      <div className="absolute" style={{ right: 22, top: 38 }}>
        <FiftyChip size={80} fontSize={32} rotate={-8} />
      </div>
      <div className="absolute" style={{ right: 86, top: 26 }}>
        <FiveChip size={44} fontSize={16} rotate={16} />
      </div>
    </PosterShellLandscape>
  );
}
